/**
 * LocationSpinController
 * Generic drag-to-rotate controller for any focused location model.
 *
 * Uses quaternion-based arcball (trackball) rotation so that drag direction
 * always matches the visual result regardless of the model's current orientation.
 * Dragging right always rotates the visible face to the right; dragging up always
 * tilts it upward — even when the model is flipped or rotated far from its rest pose.
 *
 * Rotation axes are derived from the camera's world-space right and up vectors each
 * frame, so the behaviour is always relative to what the viewer sees.
 *
 * Inertia coast after release uses the same camera-relative axis stored at the
 * moment the pointer is lifted, so the spin continues in the correct direction.
 *
 * Spring return goes back to the initialRotation quaternion (not identity) so
 * models always return to their configured rest pose.
 *
 * Props:
 *   groupRef        — ref to the Three.js Group to rotate
 *   isFocused       — boolean; true while this location is in focus
 *   initialRotation — [x, y, z] Euler angles (radians) for the rest pose; defaults [0,0,0]
 */

import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Euler, Quaternion, Vector3 } from 'three'

const DRAG_SENSITIVITY   = 0.005  // radians per pixel
const FRICTION           = 0.015  // velocity fraction remaining after 1s (lower = longer coast)
const RETURN_SMOOTH_TIME = 0.5    // seconds to spring back to rest pose after unfocus
const LOCKED_SMOOTH_TIME = 0.45   // seconds to ease to rest pose while locked (focus snap + part-to-part SLS rotation) — higher = slower/gentler
const VELOCITY_WINDOW    = 120    // ms of pointer history used for release velocity
const FLIP_HYSTERESIS_DEG = 6     // dead-band around the flip edges to prevent chatter

export function LocationSpinController({ groupRef, isFocused, initialRotation = [0, 0, 0], extraWorldYRad = 0, lockToWorldY = false, lockToWorldZ = false, onYFlip = null, flipOffsetDeg = 0, locked = false, onReturnComplete = null, snapToRestRef = null, holdRestWhenIdle = false }) {
  const { gl, camera } = useThree()
  const lockedRef      = useRef(locked)
  const lockToWorldZRef = useRef(lockToWorldZ)
  const flippedRef     = useRef(false)

  useEffect(() => { lockedRef.current = locked }, [locked])
  useEffect(() => { lockToWorldZRef.current = lockToWorldZ }, [lockToWorldZ])

  // Current orientation as a quaternion — the source of truth
  const quatRef = useRef(new Quaternion())

  // Rest-pose quaternion derived from initialRotation prop
  const restQuatRef = useRef(new Quaternion())

  // Angular velocity for inertia coast: { axis: Vector3, speed: number (rad/s) }
  const angVelRef = useRef({ axis: new Vector3(0, 1, 0), speed: 0 })

  const isDraggingRef    = useRef(false)
  const returningRef     = useRef(false)
  const snapReturningRef = useRef(false)
  const lastPosRef       = useRef({ x: 0, y: 0 })

  // Rolling buffer of { t, dx, dy } — used to compute release velocity
  const samplesRef = useRef([])

  // Recompute restQuat whenever initialRotation (or the extra world-Y spin) changes.
  // The extra world-Y angle is premultiplied onto the base pose — the SAME transform
  // the user's drag applies (lockToWorldY premultiplies a world-Y quat), so a per-part
  // spin orients the model around its drag axis without touching the base tilt.
  useEffect(() => {
    buildRestQuat(restQuatRef.current, initialRotation, extraWorldYRad)
    // If not currently in a user interaction, also snap current quat to rest pose
    // (so the model starts at the right orientation on first focus)
    if (!isFocused && !returningRef.current) {
      quatRef.current.copy(restQuatRef.current)
      if (groupRef.current) {
        groupRef.current.quaternion.copy(quatRef.current)
      }
    } else if (isFocused) {
      // Focused (e.g. locked while a part is selected): the rest pose changed —
      // switching SLS parts updates extraWorldYRad. Re-arm the return spring so the
      // model rotates ITSELF to the new pose. Without this, restQuat changes but
      // nothing drives quatRef toward it once the initial lock-spring has settled,
      // so the model stays put and only the camera target (look-at) moves.
      returningRef.current = true
    }
  }, [initialRotation[0], initialRotation[1], initialRotation[2], extraWorldYRad]) // eslint-disable-line react-hooks/exhaustive-deps

  // On mount, force the group to the rest pose. The model can mount/remount when
  // its visibility gate toggles (e.g. SLS unmounts entering Artemis Scene 2 and
  // remounts on return home); without this the group would briefly show its raw
  // JSX rotation (identity) until a later frame applies restQuat, which sometimes
  // left it stuck at the wrong orientation.
  useEffect(() => {
    if (isFocused) return
    buildRestQuat(restQuatRef.current, initialRotation, extraWorldYRad)
    quatRef.current.copy(restQuatRef.current)
    returningRef.current = false
    if (groupRef.current) groupRef.current.quaternion.copy(restQuatRef.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (locked) {
      isDraggingRef.current   = false
      angVelRef.current.speed = 0
      returningRef.current    = true
    } else {
      returningRef.current = false
    }
  }, [locked])

  useEffect(() => {
    // Invalidate the cached flip state on every focus change so the controller
    // re-emits onYFlip with the correct value once active again (e.g. returning
    // to the deployed view after the assembly was snapped back to rest).
    flippedRef.current = null

    if (!isFocused) {
      isDraggingRef.current     = false
      samplesRef.current        = []
      angVelRef.current.speed   = 0
      returningRef.current      = true
      return
    }

    returningRef.current = false

    const canvas = gl.domElement

    const onPointerDown = (e) => {
      if (lockedRef.current) return
      isDraggingRef.current       = true
      lastPosRef.current          = { x: e.clientX, y: e.clientY }
      samplesRef.current          = []
      angVelRef.current.speed     = 0
      canvas.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e) => {
      if (!isDraggingRef.current || lockedRef.current) return
      const now = performance.now()
      const dx  = e.clientX - lastPosRef.current.x
      const dy  = e.clientY - lastPosRef.current.y
      lastPosRef.current = { x: e.clientX, y: e.clientY }

      // Apply an immediate arcball rotation using camera-relative axes
      applyArcballDelta(quatRef.current, camera, dx, dy, DRAG_SENSITIVITY, lockToWorldY, lockToWorldZRef.current)

      // Push to rolling buffer and prune old samples
      samplesRef.current.push({ t: now, dx, dy })
      const cutoff = now - VELOCITY_WINDOW
      samplesRef.current = samplesRef.current.filter(s => s.t >= cutoff)
    }

    const onPointerUp = (e) => {
      isDraggingRef.current = false
      canvas.releasePointerCapture(e.pointerId)

      // Compute release velocity from the rolling window, measured from the
      // RELEASE moment — so a pause before lifting (no move events fire) leaves
      // an empty window and the model stops dead instead of coasting on stale
      // motion. dt spans oldest kept sample → release time, so a gap between the
      // last movement and release decays the released speed toward zero.
      const now = performance.now()
      const samples = samplesRef.current.filter(s => s.t >= now - VELOCITY_WINDOW)
      if (samples.length >= 1) {
        const dt = (now - samples[0].t) / 1000
        if (dt > 0) {
          const totalDx = samples.reduce((s, p) => s + p.dx, 0)
          const totalDy = samples.reduce((s, p) => s + p.dy, 0)

          // Build a combined camera-relative axis for the accumulated drag
          const axis = getCameraRelativeAxis(camera, totalDx, totalDy, lockToWorldY, lockToWorldZRef.current)
          const speed = (Math.sqrt(totalDx * totalDx + totalDy * totalDy) * DRAG_SENSITIVITY) / dt
          angVelRef.current.axis.copy(axis)
          angVelRef.current.speed = speed
        }
      }
      samplesRef.current = []
    }

    canvas.addEventListener('pointerdown',   onPointerDown)
    canvas.addEventListener('pointermove',   onPointerMove)
    canvas.addEventListener('pointerup',     onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)

    return () => {
      canvas.removeEventListener('pointerdown',   onPointerDown)
      canvas.removeEventListener('pointermove',   onPointerMove)
      canvas.removeEventListener('pointerup',     onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
      isDraggingRef.current = false
    }
  }, [isFocused, gl]) // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, delta) => {
    if (!groupRef.current) return

    if (snapToRestRef && snapToRestRef.current) {
      snapToRestRef.current   = false
      isDraggingRef.current   = false
      angVelRef.current.speed = 0
      snapReturningRef.current = true
    }

    if (snapReturningRef.current) {
      if (isDraggingRef.current) {
        snapReturningRef.current = false
      } else {
        const t = 1 - Math.pow(2, -delta / (RETURN_SMOOTH_TIME / 2))
        quatRef.current.slerp(restQuatRef.current, t)
        groupRef.current.quaternion.copy(quatRef.current)
        if (quatRef.current.angleTo(restQuatRef.current) < 0.001) {
          quatRef.current.copy(restQuatRef.current)
          groupRef.current.quaternion.copy(restQuatRef.current)
          snapReturningRef.current = false
        }
        return
      }
    }

    if (returningRef.current) {
      if (isDraggingRef.current && !lockedRef.current) {
        returningRef.current = false
      } else {
        // Smooth slerp to rest pose. When locked (SLS part focus + part-to-part
        // rotation) use the gentler LOCKED_SMOOTH_TIME so the model eases round
        // rather than snapping; unfocus return uses the slower RETURN_SMOOTH_TIME.
        const smoothTime = lockedRef.current ? LOCKED_SMOOTH_TIME : RETURN_SMOOTH_TIME
        const t = 1 - Math.pow(2, -delta / smoothTime)
        quatRef.current.slerp(restQuatRef.current, t)

        // Threshold tightened to match snapReturningRef's 0.001 (was 0.01 ≈ 0.57°).
        // The exponential ease-out never mathematically reaches restQuat — it's cut
        // off here and the remainder is hard-set in one frame. At the tight FOV used
        // while a sub-component is focused (e.g. SLS parts, fov ~3), even a fraction
        // of a degree is magnified on screen, so the old 0.57° cutoff read as a
        // visible snap right at the end of the rotation.
        const angleDiff = quatRef.current.angleTo(restQuatRef.current)
        if (angleDiff < 0.001) {
          quatRef.current.copy(restQuatRef.current)
          returningRef.current = false
          if (lockedRef.current && onReturnComplete) onReturnComplete()
        }

        groupRef.current.quaternion.copy(quatRef.current)
        return
      }
    }

    if (!isFocused) {
      // Idle & unfocused: optionally hold the rest pose (opt-in via holdRestWhenIdle,
      // used by the SLS). Models that stay MOUNTED across a mode change (desktop
      // keepArtemisModelsResident — the SLS stays mounted through simulate-mission)
      // don't re-run the mount effect on return, and the controller otherwise stops
      // writing the group quaternion when idle. If anything reset the group's
      // transform meanwhile (e.g. a re-render re-seeding the JSX rotation), it'd be
      // left at a stale pose until the next focus (the "click EXPLORE SLS to fix it"
      // bug). Re-asserting restQuat keeps it correct without a focus round-trip.
      // quatRef is already at rest, so this is a no-op write in the normal case.
      // Opt-in so the more delicate Orion/satellite states are byte-for-byte unchanged.
      if (holdRestWhenIdle && !isDraggingRef.current && !returningRef.current && !snapReturningRef.current) {
        groupRef.current.quaternion.copy(restQuatRef.current)
      }
      return
    }

    // Coast: friction decay on angular speed, applied only when not dragging
    if (!isDraggingRef.current) {
      const friction = Math.pow(FRICTION, delta)
      angVelRef.current.speed *= friction

      if (angVelRef.current.speed > 0.0001) {
        const angle = angVelRef.current.speed * delta
        const dq = _tempQuat.setFromAxisAngle(angVelRef.current.axis, angle)
        quatRef.current.premultiply(dq)
      }
    }

    groupRef.current.quaternion.copy(quatRef.current)
    if (lockToWorldY && onYFlip) {
      _flipEuler.setFromQuaternion(quatRef.current, 'YXZ')
      // flipOffsetDeg rotates the flip band without affecting other callers (SLS
      // passes 0). Default band is the 180° window centred at -45°.
      const worldYDeg = _flipEuler.y * (180 / Math.PI) - flipOffsetDeg
      const inside = worldYDeg <= 45 && worldYDeg >= -135
      // Hysteresis dead-band so a model resting on an edge can't chatter the flip
      // state every frame (each toggle re-renders the consumer).
      const prev = flippedRef.current
      const nearEdge = prev !== null &&
        Math.min(Math.abs(worldYDeg - 45), Math.abs(worldYDeg + 135)) < FLIP_HYSTERESIS_DEG
      const flipped = nearEdge ? prev : inside
      if (flipped !== flippedRef.current) {
        flippedRef.current = flipped
        onYFlip(flipped)
      }
    }
  })

  return null
}

// ---------------------------------------------------------------------------
// Helpers (module-level, no allocations per frame)
// ---------------------------------------------------------------------------

const _tempQuat  = new Quaternion()
const _flipEuler = new Euler()
const _rightVec  = new Vector3()
const _upVec     = new Vector3()
const _axis      = new Vector3()
const _dqX       = new Quaternion()
const _dqY       = new Quaternion()
const _worldY    = new Vector3(0, 1, 0)
const _worldZ    = new Vector3(0, 0, 1)
const _baseEuler   = new Euler()
const _extraYQuat  = new Quaternion()

// Build the rest quaternion = world-Y(extraWorldYRad) ∘ base(initialRotation Euler).
// The extra world-Y spin is premultiplied — identical to how the drag applies a
// world-Y rotation (quat.premultiply(worldYquat)) — so it spins the model around its
// drag axis on top of the base tilt, instead of replacing the whole pose.
function buildRestQuat(out, initialRotation, extraWorldYRad) {
  _baseEuler.set(initialRotation[0], initialRotation[1], initialRotation[2], 'XYZ')
  out.setFromEuler(_baseEuler)
  if (extraWorldYRad) {
    _extraYQuat.setFromAxisAngle(_worldY, extraWorldYRad)
    out.premultiply(_extraYQuat)
  }
  return out
}

function applyArcballDelta(quat, camera, dx, dy, sensitivity, lockToWorldY, lockToWorldZ) {
  if (lockToWorldZ) {
    // Only horizontal drag, always around world Z
    _dqY.setFromAxisAngle(_worldZ, dx * sensitivity)
    quat.premultiply(_dqY)
    return
  }

  if (lockToWorldY) {
    // Only horizontal drag, always around world Y
    _dqY.setFromAxisAngle(_worldY, dx * sensitivity)
    quat.premultiply(_dqY)
    return
  }

  // Free arcball — camera-relative right + up
  camera.matrixWorld.extractBasis(_rightVec, _upVec, _axis /* ignored */)
  _rightVec.normalize()
  _upVec.normalize()

  _dqY.setFromAxisAngle(_upVec,    dx * sensitivity)
  _dqX.setFromAxisAngle(_rightVec, dy * sensitivity)
  quat.premultiply(_dqY).premultiply(_dqX)
}

function getCameraRelativeAxis(camera, dx, dy, lockToWorldY, lockToWorldZ) {
  if (lockToWorldZ) {
    return dx >= 0 ? _worldZ.clone() : _worldZ.clone().negate()
  }

  if (lockToWorldY) {
    // Flip axis to match drag direction so inertia continues the correct way
    return dx >= 0 ? _worldY.clone() : _worldY.clone().negate()
  }

  camera.matrixWorld.extractBasis(_rightVec, _upVec, _axis /* ignored */)
  _rightVec.normalize()
  _upVec.normalize()

  const ax = _upVec.clone().multiplyScalar(dx)
  const ay = _rightVec.clone().multiplyScalar(dy)
  return ax.add(ay).normalize()
}
